/**
 * Client-side CSV generation and download utility.
 */

interface CsvColumn<T> {
  /** Column header label */
  header: string;
  /** Function to extract the cell value from a row */
  accessor: (row: T) => string | number | null | undefined;
}

/**
 * Escape a CSV cell value. Wraps in quotes if the value contains commas,
 * quotes, or newlines, and doubles any existing quotes.
 */
function escapeCsvCell(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/**
 * Convert an array of data to a CSV string using the provided column definitions.
 */
export function toCsv<T>(rows: T[], columns: CsvColumn<T>[]): string {
  const header = columns.map((col) => escapeCsvCell(col.header)).join(',');

  const body = rows.map((row) =>
    columns
      .map((col) => {
        const raw = col.accessor(row);
        const value = raw == null ? '' : String(raw);
        return escapeCsvCell(value);
      })
      .join(','),
  );

  return [header, ...body].join('\n');
}

/**
 * Trigger a browser download of a CSV string.
 *
 * @param csv - The CSV content
 * @param prefix - Filename prefix, e.g. "orders" → "orders-2026-02-13.csv"
 */
export function downloadCsv(csv: string, prefix: string): void {
  const date = new Date().toISOString().slice(0, 10);
  const filename = `${prefix}-${date}.csv`;

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();

  URL.revokeObjectURL(url);
}
